import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_PATH = '/contac-analytics';

type ISODate = string;

interface PeriodoQuery {
  dataInicio: ISODate;
  dataFim: ISODate;
}

const paramsSerializer = (params: Record<string, any>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (Array.isArray(v)) v.forEach((item) => usp.append(k, String(item)));
    else usp.append(k, String(v));
  });
  return usp.toString();
};

export interface PlacaResumoQuery extends PeriodoQuery {
  placa: string;
  base_tag?: string;
}

export type PlacaResumoBloco = 'DETALHE' | 'TOTAIS';
export type PlacaResumoTipo = 'ENTRADA' | 'SAIDA';

export interface PlacaResumoItemDto {
  bloco: PlacaResumoBloco;
  placa: string;
  competencia: string;
  tipo: PlacaResumoTipo | null;
  fonte: string | null;
  classificacao: string | null;
  valor_total: number;
}

export interface PlacaResumoResponseDto {
  periodo: string;
  placa: string;
  /** false quando a placa tem movimento financeiro mas não existe no cadastro de veículos do SGA */
  cadastroSga?: boolean;

  modelo?: string | null;
  ano_modelo?: number | null;
  categoria?: string | null;
  associado?: string | null;
  cpf_associado?: string | null;
  tempo_participacao_meses?: number | null;
  regional?: string | null;
  consultor?: string | null;
  cidade?: string | null;

  detalhamento: {
    resultado: number;
    totaisEntrada: number;
    totaisSaida: number;
    [competencia: string]: PlacaResumoItemDto[] | number;
  };
}

export interface PlacaResumoQueryDto {
  placa: string;
  dataInicio: string;
  dataFim: string;
  baseTag?: string;
}

export type AlertColor = 'VERDE' | 'LARANJA' | 'VERMELHO';

export interface PlacasResumoListItemDto {
  placa: string;
  base_tag?: string | null;
  modelo?: string | null;
  ano_modelo?: number | null;
  categoria?: string | null;
  associado?: string | null;
  cpf_associado?: string | null;
  tempo_participacao_meses?: number | null;
  regional?: string | null;
  consultor?: string | null;
  cidade?: string | null;
  /** false quando a placa tem movimento financeiro mas não existe no cadastro de veículos do SGA */
  cadastro_sga?: boolean;
  // Para RELACIONAMENTO o backend remove os totais e mascara resultado com 'ESCONDIDO' + alerta
  totaisEntrada?: number;
  totaisSaida?: number;
  resultado: number | 'ESCONDIDO';
  alerta?: AlertColor;
}

export interface PlacasResumoListResponse {
  page: number;
  limit: number;
  total: number;
  entradasTotais?: { valor: number };
  despesasTotais?: { valor: number };
  resultadoTotal: { valor: number | 'ESCONDIDO'; alerta?: AlertColor };
  items: PlacasResumoListItemDto[];
}

export enum PlacasResumoOrder {
  lucro = 'lucro',
  prejuizo = 'prejuizo',
}

/** Restringe a lista pelo cadastro de veículos do SGA. */
export enum PlacasResumoCadastroSga {
  com = 'com',
  sem = 'sem',
}

export interface PlacasResumoListaQueryDto {
  dataInicio: string;
  dataFim: string;
  page?: number;
  limit?: number;
  baseTag?: string;
  order?: PlacasResumoOrder;
  codigoVoluntario?: string;
  consultorNome?: string;
  consultorCpf?: string;
  categoria?: string;
  regional?: string;
  placa?: string;
  cpfAssociado?: string;
  associadoNome?: string;
  resultadoMin?: number;
  resultadoMax?: number;
  cadastroSga?: PlacasResumoCadastroSga;
}

export interface CategoriaResumoQueryDto {
  categoria: string;
  dataInicio: string;
  dataFim: string;
}

export interface CategoriasResumoListaQueryDto {
  dataInicio: string;
  dataFim: string;
  page?: number;
  limit?: number;
  order?: 'lucro' | 'prejuizo';
  categorias?: string[];
}

export interface CategoriaResumoItemDto {
  ordem: number;
  linha: 'TOTAL' | 'CLASSIFICACAO';
  categoria: string;
  competencia: string;
  classificacao: string | null;
  total_entradas: number;
  total_saidas: number;
  saldo: number;
}

export interface CategoriaResumoListItemDto {
  categoria: string;
  totaisEntrada: number;
  totaisSaida: number;
  resultado: number;
  quantidadeAtivo: number;
  quantidadeInativo: number;
  placasAtivas?: number;
  placasPeriodo?: number;
  competencias: Record<string, CategoriaResumoItemDto[]>;
}

export interface ValorTotalDto {
  valor: number;
}

export interface CategoriasResumoListResponseDto {
  page: number;
  limit: number;
  total: number;

  entradasTotais: ValorTotalDto;
  despesasTotais: ValorTotalDto;
  resultadoTotal: ValorTotalDto;

  items: CategoriaResumoListItemDto[];
}

export interface ConsultorResumoQueryDto {
  codigo_consultor: string;
  dataInicio: string;
  dataFim: string;
}

export interface ConsultorResumoLinhaDto {
  ordem: number;
  linha: 'TOTAL' | 'CLASSIFICACAO';
  consultor_codigo: string;
  consultor: string;
  competencia: string;
  classificacao: string | null;
  total_entradas: number;
  total_saidas: number;
  saldo: number;
}

export interface ConsultorResumoListItemDto {
  consultor_codigo: string;
  consultor: string;
  totaisEntrada: number;
  totaisSaida: number;
  resultado: number;
  competencias: Record<string, ConsultorResumoLinhaDto[]>;
}

export interface ConsultoresResumoListResponseDto {
  page: number;
  limit: number;
  total: number;
  items: ConsultorResumoListItemDto[];
}

export interface ConsultoresResumoListaQueryDto {
  dataInicio: string;
  dataFim: string;
  page?: number;
  limit?: number;
  order?: 'lucro' | 'prejuizo';
  codigo_consultor?: string;
  consultor?: string;
}

export const fetchPlacaResumo = async (
  query: PlacaResumoQueryDto,
): Promise<PlacaResumoResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/placa/resumo`;

    const params = {
      placa: String(query.placa ?? ''),
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.baseTag ? { baseTag: query.baseTag } : {}),
    };

    const response = await axios.get<PlacaResumoResponseDto>(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar resumo mensal por placa:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(error.response?.data?.message || 'Erro ao buscar resumo por placa');
    }

    throw new Error('Erro desconhecido');
  }
};

export const fetchPlacasResumoLista = async (
  query: PlacasResumoListaQueryDto,
): Promise<PlacasResumoListResponse> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/placas/resumo`;

    const params = {
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.page !== undefined ? { page: query.page } : {}),
      ...(query.limit !== undefined ? { limit: query.limit } : {}),
      ...(query.baseTag ? { base_tag: query.baseTag } : {}),
      ...(query.order ? { order: query.order } : {}),
      ...(query.codigoVoluntario ? { codigoVoluntario: query.codigoVoluntario } : {}),
      ...(query.consultorNome ? { consultorNome: query.consultorNome } : {}),
      ...(query.consultorCpf ? { consultorCpf: query.consultorCpf } : {}),
      ...(query.categoria ? { categoria: query.categoria } : {}),
      ...(query.regional ? { regional: query.regional } : {}),
      ...(query.placa ? { placa: query.placa } : {}),
      ...(query.cpfAssociado ? { cpfAssociado: query.cpfAssociado } : {}),
      ...(query.associadoNome ? { associadoNome: query.associadoNome } : {}),
      ...(query.resultadoMin !== undefined ? { resultado_min: query.resultadoMin } : {}),
      ...(query.resultadoMax !== undefined ? { resultado_max: query.resultadoMax } : {}),
      ...(query.cadastroSga ? { cadastroSga: query.cadastroSga } : {}),
    };

    const response = await axios.get<PlacasResumoListResponse>(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar lista de resumo por placas:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(error.response?.data?.message || 'Erro ao buscar lista de resumo por placas');
    }

    throw new Error('Erro desconhecido');
  }
};

export interface CategoriaResumoQuery extends PeriodoQuery {
  categoria: string;
}

export const fetchCategoriaResumo = async (query: CategoriaResumoQueryDto) => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/categoria/resumo`;

    const params = {
      categoria: query.categoria,
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
    };

    const response = await axios.get(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar resumo mensal por categoria:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(error.response?.data?.message || 'Erro ao buscar resumo por categoria');
    }

    throw new Error('Erro desconhecido');
  }
};

export interface CategoriasResumoListaQuery extends PeriodoQuery {
  page?: number;
  limit?: number;

  categorias?: string[];
  sortBy?: 'lucro' | 'prejuizo' | 'resultado';
  sortOrder?: 'asc' | 'desc';
}

export const fetchCategoriasResumoLista = async (
  query: CategoriasResumoListaQueryDto,
): Promise<CategoriasResumoListResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/categorias/resumo`;

    const params = {
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.page !== undefined ? { page: query.page } : {}),
      ...(query.limit !== undefined ? { limit: query.limit } : {}),
      ...(query.order ? { order: query.order } : {}),
      ...(query.categorias && query.categorias.length ? { categorias: query.categorias } : {}),
    };

    const response = await axios.get<CategoriasResumoListResponseDto>(url, {
      params,
      paramsSerializer,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar lista de resumo por categorias:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar lista de resumo por categorias',
      );
    }

    throw new Error('Erro desconhecido');
  }
};

export interface ConsultorResumoQuery extends PeriodoQuery {
  codigo_consultor: string;
}

export const fetchConsultorResumo = async (query: ConsultorResumoQueryDto) => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/consultor/resumo`;

    const params = {
      codigo_consultor: query.codigo_consultor,
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
    };

    const response = await axios.get(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar resumo mensal por consultor:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(error.response?.data?.message || 'Erro ao buscar resumo por consultor');
    }

    throw new Error('Erro desconhecido');
  }
};

export interface ConsultoresResumoListaQuery extends PeriodoQuery {
  page?: number;
  limit?: number;

  codigo_consultor?: string;
  nome?: string;

  sortBy?: 'lucro' | 'prejuizo' | 'resultado';
  sortOrder?: 'asc' | 'desc';
}

export const fetchConsultoresResumoLista = async (
  query: ConsultoresResumoListaQueryDto,
): Promise<ConsultoresResumoListResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/consultores/resumo`;

    const params = {
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.page !== undefined ? { page: query.page } : {}),
      ...(query.limit !== undefined ? { limit: query.limit } : {}),
      ...(query.order ? { order: query.order } : {}),
      ...(query.codigo_consultor ? { codigo_consultor: query.codigo_consultor } : {}),
      ...(query.consultor ? { consultor: query.consultor } : {}),
    };

    const response = await axios.get<ConsultoresResumoListResponseDto>(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar lista de resumo por consultores:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar lista de resumo por consultores',
      );
    }

    throw new Error('Erro desconhecido');
  }
};

export interface RegionalResumoQueryDto {
  codigo_regional?: number;
  base_tag?: string;
  dataInicio: string;
  dataFim: string;
}

export interface RegionalResumoItemDto {
  ordem: number;
  linha: 'TOTAL' | 'CLASSIFICACAO';
  regional: string;
  competencia: string;
  classificacao: string | null;
  total_entradas: number;
  total_saidas: number;
  saldo: number;
}

export interface RegionalResumoResponseDto {
  periodo: string;
  regional: string;
  placasAtivas?: number;
  placasPeriodo?: number;
  detalhamento: {
    resultado: number;
    totaisEntrada?: number;
    totaisSaida?: number;
    alerta?: string;
    [competencia: string]: any;
  };
}

export interface RegionaisResumoListaQueryDto {
  dataInicio: string;
  dataFim: string;
  page?: number;
  limit?: number;
  order?: 'lucro' | 'prejuizo';
}

export interface RegionalResumoListItemDto {
  regional: string;
  totaisEntrada: number;
  totaisSaida: number;
  resultado: number;
  competencias: Record<string, RegionalResumoItemDto[]>;
}

export interface RegionaisResumoListResponseDto {
  page: number;
  limit: number;
  total: number;
  entradasTotais: { valor: number };
  despesasTotais: { valor: number };
  resultadoTotal: { valor: number };
  items: RegionalResumoListItemDto[];
}

export const fetchRegionalResumo = async (
  query: RegionalResumoQueryDto,
): Promise<RegionalResumoResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/regional/resumo`;

    const params = {
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.codigo_regional !== undefined ? { codigo_regional: query.codigo_regional } : {}),
      ...(query.base_tag !== undefined ? { base_tag: query.base_tag } : {}),
    };

    const response = await axios.get<RegionalResumoResponseDto>(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar resumo por regional:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(error.response?.data?.message || 'Erro ao buscar resumo por regional');
    }

    throw new Error('Erro desconhecido');
  }
};

// ─── Cooperativa ─────────────────────────────────────────────────────────────

export interface CooperativaResumoQueryDto {
  codigo_cooperativa: string;
  dataInicio: string;
  dataFim: string;
}

export interface CooperativaResumoResponseDto {
  periodo: string;
  cooperativa: string;
  codigoCooperativa: string;
  placasAtivas?: number;
  placasPeriodo?: number;
  detalhamento: Record<string, any>;
}

export interface CooperativaResumoItemDto {
  ordem: number;
  linha: 'TOTAL' | 'CLASSIFICACAO';
  cooperativa: string;
  competencia: string;
  classificacao: string | null;
  total_entradas: number;
  total_saidas: number;
  saldo: number;
}

export interface CooperativaResumoListItemDto {
  codigo_cooperativa: string;
  cooperativa: string;
  totaisEntrada: number;
  totaisSaida: number;
  resultado: number;
  competencias: Record<string, CooperativaResumoItemDto[]>;
}

export interface CooperativasResumoListResponseDto {
  page: number;
  limit: number;
  total: number;
  entradasTotais?: { valor: number };
  despesasTotais?: { valor: number };
  resultadoTotal: { valor: number | 'ESCONDIDO' };
  items: CooperativaResumoListItemDto[];
}

export interface CooperativasResumoListaQueryDto {
  dataInicio: string;
  dataFim: string;
  page?: number;
  limit?: number;
  order?: 'lucro' | 'prejuizo';
}

export const fetchCooperativaResumo = async (
  query: CooperativaResumoQueryDto,
): Promise<CooperativaResumoResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/cooperativa/resumo`;
    const response = await axios.get<CooperativaResumoResponseDto>(url, {
      params: {
        codigo_cooperativa: query.codigo_cooperativa,
        dataInicio: query.dataInicio,
        dataFim: query.dataFim,
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) throw new Error(error.response?.data?.message || 'Erro ao buscar resumo por cooperativa');
    throw new Error('Erro desconhecido');
  }
};

export const fetchCooperativasResumoLista = async (
  query: CooperativasResumoListaQueryDto,
): Promise<CooperativasResumoListResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/cooperativas/resumo`;
    const params = {
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.page !== undefined ? { page: query.page } : {}),
      ...(query.limit !== undefined ? { limit: query.limit } : {}),
      ...(query.order ? { order: query.order } : {}),
    };
    const response = await axios.get<CooperativasResumoListResponseDto>(url, {
      params,
      withCredentials: true,
    });
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) throw new Error(error.response?.data?.message || 'Erro ao buscar lista de cooperativas');
    throw new Error('Erro desconhecido');
  }
};

export const fetchRegionaisResumoLista = async (
  query: RegionaisResumoListaQueryDto,
): Promise<RegionaisResumoListResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/regionais/resumo`;

    const params = {
      dataInicio: query.dataInicio,
      dataFim: query.dataFim,
      ...(query.page !== undefined ? { page: query.page } : {}),
      ...(query.limit !== undefined ? { limit: query.limit } : {}),
      ...(query.order ? { order: query.order } : {}),
    };

    const response = await axios.get<RegionaisResumoListResponseDto>(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar lista de resumo por regionais:', error);

    if (axios.isAxiosError(error)) {
      console.error('❌ Axios error response:', error.response);
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar lista de resumo por regionais',
      );
    }

    throw new Error('Erro desconhecido');
  }
};

export interface ContacStatusResponseDto {
  ultimaAtualizacao: string | null;
}

export const fetchContacStatus = async (): Promise<ContacStatusResponseDto> => {
  try {
    const url = `${BASE_URL}${BASE_PATH}/status`;
    const response = await axios.get<ContacStatusResponseDto>(url, {
      withCredentials: true,
    });
    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar status de atualização do Contac:', error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar status do Contac');
    }
    throw new Error('Erro desconhecido');
  }
};


// ---------------------------------------------------------------------------------------------
// A régua das cores (contac/at-13, ADR 0007 do Contac)
// ---------------------------------------------------------------------------------------------

export interface CorteDaRegua {
  meses: number;
  banda: number;
  verdeAcimaDe: number;
  vermelhoAbaixoDe: number;
}

export interface ReguaAlertaResponse {
  rPorMes: number;
  formula: string;
  cortes: CorteDaRegua[];
  /** As medições que ancoram o valor por mês. É o que a tela de administração exibe. */
  ancoragem: {
    medidoEm: string;
    janelaMedida: string;
    base: string;
    componentes: { nome: string; p25: number; mediana: number; p75: number; observacao: string }[];
    resultadoMensal: {
      situacao: string;
      placaMeses: number;
      p25: number;
      mediana: number;
      p75: number;
    }[];
    distribuicaoPorCorte: {
      meses: number;
      placas: number;
      verdeAntes: number;
      verdeDepois: number;
      vermelhoDepois: number;
    }[];
    vermelhoComCustoDireto: { meses: number; percentual: number }[];
    faixaAnterior: number;
  };
  vigenteDesde: string;
}

/**
 * A régua que transforma resultado em cor.
 *
 * ⚠️ Sem catch aqui de propósito (regra da at-60): quem trata o erro é a tela, que precisa dizer
 * "não consegui carregar a régua" em vez de exibir um número inventado ao lado da legenda.
 */
export const fetchReguaAlerta = async (): Promise<ReguaAlertaResponse> => {
  const { data } = await axios.get<ReguaAlertaResponse>(`${BASE_URL}${BASE_PATH}/regua-alerta`, {
    withCredentials: true,
  });
  return data;
};
